import {InputLabel, makeStyles} from '@material-ui/core';
import {InputProps} from 'Quoting/models/FormInputs';
import {cleanHelperText, createUniqueFormFieldId, isDefined, isQuoteNotEditable} from 'Quoting/utils/utilities';
import classNames from 'classnames';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {useFormValidation} from '../hooks/useFormValidation';
import {QuoteDatePicker} from './QuoteDatePicker';
import {QuoteDropdown} from './QuoteDropdown';
import {QuoteFormControlElement} from './QuoteFormControlElement';
import {QuoteDropdownV2} from './QuoteFormDropdownV2';
import {QuoteAutocompleteMultiSelect} from './QuoteFormMultiSelect';
import {QuoteTextField} from './QuoteTextField';
import {useInputLabelStyles} from './styles';

type QuoteFormElementPropsMap = {
  Text: React.ComponentProps<typeof QuoteTextField>;
  FormControl: React.ComponentProps<typeof QuoteFormControlElement>;
  Select: React.ComponentProps<typeof QuoteDropdown>;
  DatePicker: React.ComponentProps<typeof QuoteDatePicker>;
  SelectV2: React.ComponentProps<typeof QuoteDropdownV2>;
  MultiSelect: React.ComponentProps<typeof QuoteAutocompleteMultiSelect>;
};

type QuoteFormElementProps<T extends keyof QuoteFormElementPropsMap> = Partial<QuoteFormElementPropsMap[T]> & {
  elementType: T;
  label?: React.ReactNode;
  isHidden?: boolean;
  adornment?: 'money' | 'percentage';
  formFieldId?: string;
};

const quoteFormElementMap: Record<string, React.ElementType> = {
  Text: QuoteTextField,
  FormControl: QuoteFormControlElement,
  Select: QuoteDropdown,
  DatePicker: QuoteDatePicker,
  SelectV2: QuoteDropdownV2,
  MultiSelect: QuoteAutocompleteMultiSelect,
};

const useQuoteFormElementStyles = makeStyles(() => ({
  elementStyles: (props: QuoteFormElementPropsMap[keyof QuoteFormElementPropsMap]) => ({
    backgroundColor: props.disabled ? '#E8E8E8' : 'transparent',
    color: props.disabled ? '#474747' : '#000000',
    '& > div > fieldset': {
      borderRadius: 0,
    },
  }),
}));

const getInputProps = <T extends keyof QuoteFormElementPropsMap>(
  elementProps: QuoteFormElementProps<T>
): InputProps['inputFieldProps'] => {
  const style: Partial<React.CSSProperties> = {
    color: elementProps.disabled ? '#474747' : '#000000',
    borderRadius: 0,
  };

  return {
    style,
    startAdornment: elementProps.adornment === 'money' ? '$' : '',
    endAdornment: elementProps.adornment === 'percentage' ? '%' : '',
  };
};

export const QuoteFormElement = <T extends keyof QuoteFormElementPropsMap>(
  formElementProps: QuoteFormElementProps<T>
) => {
  const {quote, quoteFormErrors, selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const {elementType, onChange, formFieldId, ...elementProps} = formElementProps;
  const inputLabelStyles = useInputLabelStyles();
  const classes = useQuoteFormElementStyles({disabled: elementProps.disabled});

  const formFieldIdentifier = createUniqueFormFieldId(formFieldId, selectedPlan?.planKey);

  const validationHelper = useFormValidation(formFieldIdentifier);

  const Component = quoteFormElementMap[elementType];

  if (!Component || elementProps.isHidden) {
    return null;
  }

  const formattedValue =
    elementProps.adornment === 'percentage' &&
    isDefined(elementProps.value) &&
    !Number.isNaN(Number(elementProps.value))
      ? (Number(elementProps.value) * 100).toFixed(0)
      : elementProps.value;

  const handleBlur = (e: any) => {
    if (elementProps?.onBlur) {
      elementProps?.onBlur?.(e);
    }
    validationHelper.handleValidation?.();
  };

  const isDisabledInputField = elementProps.disabled || isQuoteNotEditable(quote);
  const isFormControlElement = ['FormControl'].includes(elementType);
  const inputFieldProps = ['Text', 'Select', 'DatePicker', 'SelectV2'].includes(elementType)
    ? getInputProps(formElementProps)
    : undefined;

  const cssClasses = classNames(elementProps.className, {[classes.elementStyles]: !isFormControlElement});

  const helperText = cleanHelperText(
    'helperText' in elementProps ? (elementProps?.helperText as string) : quoteFormErrors?.[formFieldIdentifier]
  );

  return (
    <div>
      {elementProps.label && !isFormControlElement && (
        <InputLabel htmlFor={elementProps.id} className={inputLabelStyles.label} required={elementProps.required}>
          {elementProps.label}
        </InputLabel>
      )}
      <Component
        {...elementProps}
        inputFieldProps={inputFieldProps}
        className={cssClasses}
        disabled={isDisabledInputField}
        value={formattedValue}
        onChange={isDisabledInputField ? undefined : onChange}
        key={elementProps.id}
        onBlur={isDisabledInputField ? undefined : e => handleBlur(e)}
        error={Boolean(elementProps.error ?? quoteFormErrors?.[formFieldIdentifier])}
        helperText={helperText}
        inputProps={{
          'data-testid': elementProps.id,
          ...elementProps.inputProps,
        }}
      />
    </div>
  );
};
